import { AxiosError } from 'axios';

import type { GLOBAL_SEARCH_VIEW } from '@/features/GlobalSearch/types/searchConst';
import { getCurrentTimezone } from '@/utils/time';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '..';
import CreateWorkspaceError from './CreateWorkspaceErrorHandler';

const workspaceService = {
  fetchWorkspaces: async (): Promise<WorkspaceListItemT[]> => {
    try {
      const res = await defaultAxios.get('/workspaces');
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  fetchCredentialAPIKey: async (alias: string) => {
    try {
      const res = await defaultAxios.post(`workspaces/${alias}/apicredential`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  removeCredentialAPIKey: async (alias: string) => {
    try {
      const res = await defaultAxios.delete(
        `workspaces/${alias}/apicredential`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  fetchWorkspaceMembers: async (alias: string) => {
    try {
      const res = await defaultAxios.get(`/workspaces/${alias}/members`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  fetchWorkspaceSlackInfos: async (alias: string) => {
    try {
      const res = await defaultAxios.get(`/workspaces/${alias}/slack`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  fetchSelectedCategoriesAndTags:
    async (): Promise<FetchSelectedCategoryRes> => {
      try {
        const wa = getCurrWorkspaceAlias();
        const res = await defaultAxios.get(
          `/workspaces/${wa}/selected_categories_tags/`,
        );
        return res.data;
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
  async saveInsightCategory(
    category_ids: (number | string)[],
    tag_ids: (number | string)[],
  ) {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.patch(
        `/workspaces/${wa}/selected_categories_tags/`,
        {
          category_ids,
          tag_ids,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  createWorkspace: async (
    params: CreateWorkspaceParams,
  ): Promise<WorkspaceT> => {
    try {
      const res = await defaultAxios.post('/workspaces', {
        ...params,
        timezone: getCurrentTimezone(),
      });
      return res.data;
    } catch (e) {
      if (e instanceof AxiosError) {
        const error = new CreateWorkspaceError(e);
        throw error;
      }
      console.error(e);
      throw e;
    }
  },
  patchWorkspaceAlias: async (alias: string, isViewer: boolean) => {
    try {
      const res = await defaultAxios.put(
        `/workspaces/${alias}/regen?${isViewer ? 1 : 0}`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  patchWorkspaceMemberRole: async (
    alias: string,
    user_id: string,
    role: MEMBER_ROLE,
  ) => {
    try {
      const res = await defaultAxios.patch(`/workspaces/${alias}/role`, {
        user_id,
        role,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  kickWorkspaceMember: async (alias: string, user_id: string) => {
    try {
      await defaultAxios.put(`/workspaces/${alias}/kick`, {
        user: user_id,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  leaveWorkspaceMember: async (alias: string) => {
    try {
      await defaultAxios.put(`/workspaces/${alias}/leave`);
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  patchWorkspaceName: async (alias: string, name: string) => {
    try {
      const res = await defaultAxios.patch(`/workspaces/${alias}/name`, {
        alias,
        name,
      });
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  patchWorkspaceTimezone: async (alias: string, timezone: string) => {
    try {
      const res = await defaultAxios.patch(`/workspaces/${alias}/timezone`, {
        alias,
        timezone,
      });
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  deleteWorkspace: async (alias: string) => {
    try {
      const res = await defaultAxios.delete(`/workspaces/${alias}`);
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  createWorkspaceFieldOption: async (
    field: WorkspaceConfigsFields,
    value: string,
  ) => {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.post(`/workspaces/${wa}/${field}`, {
        [field]: value,
      });
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  deleteWorkspaceFieldOption: async (
    field: WorkspaceConfigsFields,
    value: string,
  ) => {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.put(`/workspaces/${wa}/${field}`, {
        [field]: value,
      });
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  updateAnonymizationSetting: async (state: boolean) => {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.patch(
        `/workspaces/${wa}/data_anonymization`,
        {
          data_anonymization: state,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  updatePasswordProtection: async (state: boolean) => {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.patch(
        `/workspaces/${wa}/password_protection`,
        {
          password_protection: state,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  updateLanguageSetting: async (value: string) => {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.patch(`/workspaces/${wa}/language`, {
        language: value,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  updateTopicAutoLinkFlag: async (status: boolean) => {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.patch(
        `/workspaces/${wa}/topic_auto_linking_flag`,
        {
          topic_auto_linking_flag: status,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  checkWorkspaceNameDuplication: async (name: string): Promise<boolean> => {
    const res = await defaultAxios.post(`/workspaces/name/duplication`, {
      name,
    });
    return res.data.exists;
  },

  // Search tab configuration API

  fetchSearchTabConfig: async (): Promise<{
    search_result_component: GLOBAL_SEARCH_VIEW[];
  }> => {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.get(
      `/workspaces/${wa}/search_result_components`,
    );
    return res.data;
  },
  addSearchView: async (view: GLOBAL_SEARCH_VIEW) => {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(
      `/workspaces/${wa}/search_result_components/${view}`,
    );
    return res.data;
  },
  deleteSearchView: async (view: GLOBAL_SEARCH_VIEW) => {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.delete(
      `/workspaces/${wa}/search_result_components/${view}`,
    );
    return res.data;
  },
};

export default workspaceService;
